// Valores Históricos Proyectados 2026 (Basado en tablas proporcionadas)
export const UMA = 117.31; // Se usará para tope SBC y Enferm./Maternidad y Exenciones
export const UMI = 99.58;
export const SALARIO_MINIMO = 315.04; // SMG 2026 Resto del País (440.87 Frontera)

// --- Tablas LISR VIGENTES MENSUALES 2026 ---
export const LIMITE_INFERIOR = [
    0.01, 844.60, 7168.52, 12598.03, 14644.65, 17533.65,
    35362.84, 55736.69, 106410.51, 141880.67, 425642.00
];
export const CUOTA_FIJA = [
    0.00, 16.22, 420.95, 1011.68, 1339.14, 1856.84,
    5665.16, 10457.09, 25659.23, 37009.69, 133488.54
];
export const PORCENTAJE = [
    0.0192, 0.0640, 0.1088, 0.1600, 0.1792, 0.2136,
    0.2352, 0.3000, 0.3200, 0.3400, 0.3500
];

/**
 * Retorna el Salario Base de Cotización integrando prestaciones LFT y Superiores.
 */
export const calcularSBC_Completo = (salarioDiarioBruto, prestaciones) => {
    const { 
        diasAguinaldo = 15, 
        diasVacaciones = 12, 
        primaVacacionalPorcentaje = 25,
        valesDespensaMensuales = 0,
        valesTipo = 'fijo',
        valesEstrategiaTope = 'imss',
    } = prestaciones || {};

    let valesMontoMensual = valesDespensaMensuales;
    if (valesTipo === 'porcentaje') {
        const salarioMensual = salarioDiarioBruto * 30.4;
        valesMontoMensual = salarioMensual * (valesDespensaMensuales / 100);
    }

    // 1. Integración Fija LFT (Proporción Diaria)
    const proporcionAguinaldo = diasAguinaldo / 365;
    const proporcionPrima = (diasVacaciones * (primaVacacionalPorcentaje / 100)) / 365;
    let factorIntegracionBase = 1 + proporcionAguinaldo + proporcionPrima;
    
    let sbcBase = salarioDiarioBruto * factorIntegracionBase;

    // 2. Integración Variable (Superiores)
    let integracionAdicionalDiaria = 0;

    // Vales de Despensa (Tope IMSS: Exentos hasta 40% UMA por día)
    if (valesMontoMensual > 0) {
        const valesDiarios = valesMontoMensual / 30.4;
        const topeValesIMSSDiario = UMA * 0.40;
        
        if (valesDiarios > topeValesIMSSDiario) {
            // Si la estrategia NO es 'imss', entonces el excedente sobre el 40% UMA diario integra a SBC
            if (valesEstrategiaTope !== 'imss') {
                integracionAdicionalDiaria += (valesDiarios - topeValesIMSSDiario);
            }
        }
    }

    let sbcFinal = sbcBase + integracionAdicionalDiaria;

    // Topes de Ley para IMSS
    const topeUMA = UMA * 25;
    const minimoSBC = SALARIO_MINIMO * factorIntegracionBase; 

    if (sbcFinal > topeUMA) sbcFinal = topeUMA;
    if (sbcFinal < minimoSBC) sbcFinal = minimoSBC;

    return sbcFinal;
};

/**
 * Calcula el Impuesto Sobre la Renta (ISR) a retener en un periodo específico
 * sobre la base gravable que ya trae descontadas las partes exentas.
 */
export const calcularISR = (ingresoGravadoPeriodo, diasPeriodo = 15) => {
    const factorDiario = ingresoGravadoPeriodo / diasPeriodo;
    const baseMensualizada = factorDiario * 30.4;

    let idx = 0;
    for (let i = LIMITE_INFERIOR.length - 1; i >= 0; i--) {
        if (baseMensualizada >= LIMITE_INFERIOR[i]) {
            idx = i;
            break;
        }
    }

    const baseGravable = baseMensualizada - LIMITE_INFERIOR[idx];
    const impuestoMarginal = baseGravable * PORCENTAJE[idx];
    const isrMensual = CUOTA_FIJA[idx] + impuestoMarginal;

    const isrPorDia = isrMensual / 30.4;
    return isrPorDia * diasPeriodo;
};

export const calcularIMSSObrero = (sbc, diasPeriodo = 15) => {
    const tresVecesUMA = UMA * 3;
    const tope25UMA = UMA * 25;
    const sbcTopeado = sbc > tope25UMA ? tope25UMA : sbc;
    const excedente3UMA = sbcTopeado > tresVecesUMA ? (sbcTopeado - tresVecesUMA) : 0;

    const cuotaExcedenteEspecie = excedente3UMA * diasPeriodo * 0.0040;
    const cuotaGastosMedicos = sbcTopeado * diasPeriodo * 0.00375;
    const cuotaEnDinero = sbcTopeado * diasPeriodo * 0.0025;
    const cuotaInvalidezVida = sbcTopeado * diasPeriodo * 0.00625;
    const cuotaCesantiaVejez = sbcTopeado * diasPeriodo * 0.01125;

    return cuotaExcedenteEspecie + cuotaGastosMedicos + cuotaEnDinero + cuotaInvalidezVida + cuotaCesantiaVejez;
};

export const calcularIMSSPatronal = (sbc, diasPeriodo = 15, primaRiesgo = 0.5) => {
    const tresVecesUMA = UMA * 3;
    const tope25UMA = UMA * 25;
    const sbcTopeado = sbc > tope25UMA ? tope25UMA : sbc;
    const excedente3UMA = sbcTopeado > tresVecesUMA ? (sbcTopeado - tresVecesUMA) : 0;

    const cuotaFijaEspecie = UMA * 0.2040 * diasPeriodo;
    const cuotaExcedenteEspecie = excedente3UMA * diasPeriodo * 0.0105;
    const cuotaGastosMedicos = sbcTopeado * diasPeriodo * 0.0105;
    const cuotaEnDinero = sbcTopeado * diasPeriodo * 0.0070;
    const primaRT = primaRiesgo / 100;
    const cuotaRiesgoTrabajo = sbcTopeado * diasPeriodo * primaRT;
    const cuotaInvalidezVida = sbcTopeado * diasPeriodo * 0.0175;
    const cuotaGuarderias = sbcTopeado * diasPeriodo * 0.0100;
    const cuotaRetiro = sbcTopeado * diasPeriodo * 0.0200;
    const cuotaCesantiaVejez = sbcTopeado * diasPeriodo * 0.0315;
    const aportacionInfonavit = sbcTopeado * diasPeriodo * 0.0500;

    const totalCargaPatronal = cuotaFijaEspecie + cuotaExcedenteEspecie + cuotaGastosMedicos +
        cuotaEnDinero + cuotaRiesgoTrabajo + cuotaInvalidezVida + cuotaGuarderias +
        cuotaRetiro + cuotaCesantiaVejez + aportacionInfonavit;

    return {
        cuotaFijaEspecie, cuotaExcedenteEspecie, cuotaGastosMedicos, cuotaEnDinero,
        cuotaRiesgoTrabajo, cuotaInvalidezVida, cuotaGuarderias, cuotaRetiro,
        cuotaCesantiaVejez, aportacionInfonavit, totalCargaPatronal
    };
};

export const calcularNominaEmpleado = (empleado, periodo) => {
    const diasTrabajados = periodo.diasBase - periodo.faltas - periodo.incapacidades;
    const salarioDiario = empleado.salarioBruto / 30; // Mes comercial de pago
    const salarioDiarioCotizacion = empleado.salarioBruto / 30.4; // LSS
    
    // Percepciones Ordinarias (Flujo Gravado general)
    const percepcionBrutaSueldo = salarioDiario * periodo.diasBase;
    const descuentoFaltas = salarioDiario * (periodo.faltas + periodo.incapacidades);
    const sueldoPagadoPeriodo = percepcionBrutaSueldo - descuentoFaltas;

    const prestaciones = empleado.prestaciones || {};
    let valesDespensaMensual = prestaciones.valesDespensa || 0;
    const valesTipo = prestaciones.valesTipo || 'fijo';
    const valesEstrategiaTope = prestaciones.valesEstrategiaTope || 'imss';
    const fondoAhorroTopeLey = prestaciones.fondoAhorroTopeLey !== undefined ? prestaciones.fondoAhorroTopeLey : true;
    
    // Cálculo Dinámico de Vales (Monto o %)
    const salarioMensualComercial = salarioDiario * 30;
    if (valesTipo === 'porcentaje') {
        valesDespensaMensual = salarioMensualComercial * (valesDespensaMensual / 100);
    }

    // Topes de Vales según Ley
    const topeValesMensualIMSS = UMA * 0.40 * 30.4; // ~1426.50
    const topeValesMensualISR = UMA * 30.4; // ~3566.22
    let excedenteValesMensualISR = 0; // Lo que va a gravar
    
    // Aplicar tope al beneficio bruto dependiendo de la estrategia
    if (valesEstrategiaTope === 'imss') {
        if (valesDespensaMensual > topeValesMensualIMSS) valesDespensaMensual = topeValesMensualIMSS; // Truncarlo
    } else if (valesEstrategiaTope === 'isr') {
        if (valesDespensaMensual > topeValesMensualISR) valesDespensaMensual = topeValesMensualISR; // Truncarlo a tope LISR
    } else { // 'libre'
        if (valesDespensaMensual > topeValesMensualISR) {
            excedenteValesMensualISR = valesDespensaMensual - topeValesMensualISR; // Excedente a partir de 1 UMA grava ISR
        }
    }

    const valesPeriodo = (valesDespensaMensual / 30) * periodo.diasBase; 
    const excedenteValesPeriodoISR = (excedenteValesMensualISR / 30) * periodo.diasBase;

    // Cálculo Fondo de Ahorro
    let fondoAhorroPctCapturado = (prestaciones.fondoAhorro || 0) / 100;
    let excedenteFondoAhorroPct = 0;
    
    // El tope es 13% s/ley LISR
    if (fondoAhorroPctCapturado > 0.13) {
        if (fondoAhorroTopeLey) {
            fondoAhorroPctCapturado = 0.13; // Truncado al tope
        } else {
            excedenteFondoAhorroPct = fondoAhorroPctCapturado - 0.13; // Exceso a gravar
        }
    }

    // Tope monetario de Fondo: 1.3 veces UMA elevada al año (117.31 * 365 * 1.3 = $55,662.60 Anuales = $4,638.55 Mensuales)
    const topeFondoAhorroMensual = (UMA * 365 * 1.3) / 12;
    const topeFondoAhorroPeriodo = (topeFondoAhorroMensual / 30.4) * periodo.diasBase;
    
    // Aportación y Excedente
    let fondoAhorroAportacionEmpresa = sueldoPagadoPeriodo * fondoAhorroPctCapturado; 
    let retencionFondoAhorroEmpleado = sueldoPagadoPeriodo * fondoAhorroPctCapturado;
    
    // Verificación de tope monetario si se superó e importa truncarlo
    if (fondoAhorroAportacionEmpresa > topeFondoAhorroPeriodo) {
        if (fondoAhorroTopeLey) {
            fondoAhorroAportacionEmpresa = topeFondoAhorroPeriodo;
            retencionFondoAhorroEmpleado = topeFondoAhorroPeriodo;
        }
    }

    // La retención total en el recibo (Deducción) es la suma de su aportación + la del patrón (para netear la percepción)
    let retencionTotalFondoRecibo = fondoAhorroAportacionEmpresa + retencionFondoAhorroEmpleado;

    let excedenteFondoAhorroPeriodo = 0;
    if (!fondoAhorroTopeLey && excedenteFondoAhorroPct > 0) {
       excedenteFondoAhorroPeriodo = sueldoPagadoPeriodo * excedenteFondoAhorroPct;
    }

    // SBC IMSS Integrando Múltiples Excedentes
    const sbc = calcularSBC_Completo(salarioDiarioCotizacion, {
        diasAguinaldo: prestaciones.diasAguinaldo || 15,
        diasVacaciones: prestaciones.diasVacaciones || 12,
        primaVacacionalPorcentaje: prestaciones.primaVacacionalPorcentaje || 25,
        valesDespensaMensuales: valesDespensaMensual, // Envía el original monetario ya truncado
        valesTipo: 'fijo', 
        valesEstrategiaTope
    });

    // LISR Exenciones
    // Sumar a la base gravable los excedentes no topados
    let baseGravableISR = sueldoPagadoPeriodo; 
    if (excedenteValesPeriodoISR > 0) baseGravableISR += excedenteValesPeriodoISR;
    if (excedenteFondoAhorroPeriodo > 0) baseGravableISR += excedenteFondoAhorroPeriodo;
    
    const retencionISR = calcularISR(baseGravableISR, diasTrabajados > 0 ? diasTrabajados : 1);
    const retencionIMSS = calcularIMSSObrero(sbc, diasTrabajados);

    const infonavitMonto = periodo.descuentoInfonavit || 0;
    const prestamoMonto = periodo.descuentoPrestamo || 0;

    // Total Efectivo Transferible a Cuenta Bancaria del Empleado
    // Nota: El neto excluye vales porque se pagan en Monedero.
    const transferenciaEfectivoBancaria = (sueldoPagadoPeriodo + fondoAhorroAportacionEmpresa) 
                              - retencionISR 
                              - retencionIMSS 
                              - infonavitMonto 
                              - prestamoMonto 
                              - retencionTotalFondoRecibo;
                              
    // Remuneración Total Completa = Bancos + Monedero Vales
    const netoPagarTotal = transferenciaEfectivoBancaria + valesPeriodo;

    return {
        salarioDiario,
        sbc,
        diasTrabajados,
        percepcionesDesglose: {
            sueldo: sueldoPagadoPeriodo,
            valesDespensa: valesPeriodo,
            fondoAhorroAportPatron: fondoAhorroAportacionEmpresa
        },
        totalPercepciones: sueldoPagadoPeriodo + valesPeriodo + fondoAhorroAportacionEmpresa, // CFDI
        baseGravableISR,
        deducciones: {
            isr: retencionISR,
            imss: retencionIMSS,
            faltasMonto: descuentoFaltas,
            infonavit: infonavitMonto,
            prestamos: prestamoMonto,
            fondoAhorroRetencion: retencionTotalFondoRecibo
        },
        totalDeduccionesVal: retencionISR + retencionIMSS + infonavitMonto + prestamoMonto + retencionTotalFondoRecibo,
        netoPagar: netoPagarTotal, // Historico y principal = Todo
        transferenciaBancaria: transferenciaEfectivoBancaria // Nuevo campo para visual y API pagos
    };
};
